import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import UserService from '../../../services/UserService/UserService'

import { Button, Chip, Grid, Link, Paper, Stack, Typography } from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import TrailStewardForm from '../shared/TrailSteward.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { TrailStewardService } from '../../../services/TrailStewardService/TrailStewardService'
import { TrailSteward } from '../../../models/TrailSteward.model'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailStewardSectionIds, trailStewardSections } from '../shared/TrailStewardSections'

const TrailStewardDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailSteward])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailStewardService = useMemo(() => new TrailStewardService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail Steward`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: trailSteward } = useQuery(
    [
      'TrailStewardService.get',
      {
        id: Number(id),
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailStewardService.get({
        id: Number(id),
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail steward.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (trailSteward?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailStewardService
        .delete(trailSteward.id)
        .then(() => {
          SnackbarContext.show(`Trail Steward deleted successfully. You will now be redirected.`)

          // after a delay of 1 second, redirect to the trail steward list page
          setTimeout(() => {
            navigate(`/trail-steward`)
            queryClient.removeQueries([
              'TrailStewardService.get',
              {
                id: Number(id),
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail Steward failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const sectionById = (sectionId: TrailStewardSectionIds) =>
    trailStewardSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailStewardSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {trailSteward && (
        <FormDialog<TrailSteward>
          open={isEditing}
          entity={trailSteward}
          entityLabel="Trail Steward"
          onClose={closeEditor}
        >
          <TrailStewardForm
            trailSteward={trailSteward}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onDelete={handleDelete}
        title="Confirm Delete"
        content="Are you sure you want to delete this trail steward?"
      />
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trailSteward && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Trail Steward',
                          href: '/trail-steward',
                        },
                      ]
                      breadcrumbs.push({ label: `Trail Steward ID: ${trailSteward.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={trailSteward.name}
                  subtitle={[trailSteward.region?.code, `ID ${trailSteward.id}`].filter(Boolean).join(' · ')}
                  meta={[
                    `Updated ${formatDate(trailSteward.updatedAt)}`,
                    `Created ${formatDate(trailSteward.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailStewardSectionIds.general)}
                      onEdit={editSectionHandler(TrailStewardSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Phone">
                          {trailSteward.phone ? (
                            <Link component="a" href={`tel:${trailSteward.phone}`}>
                              {trailSteward.phone}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                        <DetailField label="Email">
                          {trailSteward.email ? (
                            <Link component="a" href={`mailto:${trailSteward.email}`}>
                              {trailSteward.email}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Trail Systems (read-only) ------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection section={sectionById(TrailStewardSectionIds.systems)}>
                      {trailSteward.trailSystems && trailSteward.trailSystems.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {trailSteward.trailSystems.map((trailSystem) => (
                            <Chip
                              key={trailSystem.id}
                              size="small"
                              variant="outlined"
                              clickable
                              component={Link}
                              href={`/trail-systems/${trailSystem.id}`}
                              label={trailSystem.name}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body1" color="grey.500">
                          No trail systems available.
                        </Typography>
                      )}
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailStewardDetail
